import { useState } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";

const PERIODS = [
  { key: "all", label: "Total" },
  { key: "today", label: "Hoje" },
  { key: "week", label: "Semana" },
  { key: "last7", label: "Últimos 7 dias" },
  { key: "month", label: "Mês" },
  { key: "last30", label: "Últimos 30 dias" },
  { key: "year", label: "Ano" },
  { key: "custom", label: "Personalizado" },
] as const;

interface SupplierPeriodFilterProps {
  period?: string;
  onChange?: (period: string) => void;
  customRange?: { from: string; to: string };
  onCustomRangeChange?: (range: { from: string; to: string }) => void;
}

export function SupplierPeriodFilter({
  period = "all",
  onChange,
  customRange,
  onCustomRangeChange,
}: SupplierPeriodFilterProps) {
  const [hovered, setHovered] = useState<string | null>(null);
  const isWidePhone = useMediaQuery("(min-width: 480px)");

  return (
    <div
      style={{
        alignItems: "flex-start",
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <style>{`
        .supplier-period-custom-dates {
          align-items: center;
          background: var(--login-surface);
          border: 1px solid var(--login-border);
          border-radius: 0.75rem;
          display: grid;
          gap: 0.75rem;
          grid-template-columns: auto 1fr auto 1fr;
          padding: 0.75rem 1rem;
          width: 100%;
        }
        @media (max-width: 520px) {
          .supplier-period-custom-dates { grid-template-columns: 1fr; }
        }
        .supplier-period-scroll::-webkit-scrollbar { display: none; }
      `}</style>

      <div
        aria-label="Período dos pedidos"
        className="supplier-period-scroll"
        role="group"
        style={{
          background: "var(--login-surface)",
          border: "1px solid var(--login-border)",
          borderRadius: "0.75rem",
          display: "flex",
          gap: "0.5rem",
          maxWidth: "600px",
          overflowX: "auto",
          padding: "0.375rem",
          scrollbarWidth: "none",
          width: "100%",
        }}
      >
        {PERIODS.map(({ key, label }) => {
          const active = period === key;
          return (
            <button
              key={key}
              type="button"
              aria-pressed={active}
              onClick={() => onChange?.(key)}
              onMouseEnter={() => setHovered(key)}
              onMouseLeave={() => setHovered(null)}
              style={{
                background: active
                  ? "var(--login-accent)"
                  : hovered === key
                    ? "var(--login-border)"
                    : "transparent",
                border: "none",
                borderRadius: "0.5rem",
                boxShadow: active ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                color: active
                  ? "var(--dashboard-control-active-text)"
                  : "var(--dashboard-control-muted-text)",
                cursor: "pointer",
                flex: isWidePhone ? "1 1 auto" : "0 0 auto",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.875rem",
                fontWeight: active ? 600 : 500,
                minHeight: "44px",
                padding: "0.4rem 0.85rem",
                transition:
                  "background-color 140ms ease, color 140ms ease, transform 160ms cubic-bezier(0.22, 1, 0.36, 1)",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {period === "custom" ? (
        <div style={{ marginTop: "0.75rem", maxWidth: "600px", width: "100%" }}>
          <div className="supplier-period-custom-dates">
            <span style={dateLabelStyle}>De</span>
            <input
              aria-label="Data inicial dos pedidos"
              type="date"
              value={customRange?.from ?? ""}
              onInput={(event) =>
                onCustomRangeChange?.({
                  from: event.currentTarget.value,
                  to: customRange?.to ?? "",
                })
              }
              style={dateInputStyle}
            />
            <span style={dateLabelStyle}>até</span>
            <input
              aria-label="Data final dos pedidos"
              type="date"
              value={customRange?.to ?? ""}
              onInput={(event) =>
                onCustomRangeChange?.({
                  from: customRange?.from ?? "",
                  to: event.currentTarget.value,
                })
              }
              style={dateInputStyle}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

const dateLabelStyle: React.CSSProperties = {
  color: "var(--login-muted)",
  fontFamily: "'DM Sans', sans-serif",
  fontSize: "0.875rem",
  fontWeight: 500,
  justifySelf: "start",
};

const dateInputStyle: React.CSSProperties = {
  background: "var(--login-bg)",
  border: "1px solid var(--login-border)",
  borderRadius: "0.5rem",
  color: "var(--login-text)",
  fontFamily: "'DM Mono', monospace",
  fontSize: "0.875rem",
  minHeight: "44px",
  minWidth: 0,
  padding: "0.5rem 0.75rem",
  width: "100%",
};
